import React, { useEffect, useRef } from 'react';
import {
  Car,
  CheckCircle2,
  CreditCard,
  Info,
  LogIn,
  Mail,
  Megaphone,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { DateRange, NewUsersBreakdownResponse } from '../../lib/panelTypes';

interface Props {
  range: DateRange;
  data: NewUsersBreakdownResponse | null;
  isLoading: boolean;
  error: Error | null;
  onClose: () => void;
  onRetry: () => void;
}

interface ProgressRowProps {
  label: string;
  count: number;
  total: number;
  icon?: React.ComponentType<{ className?: string }>;
  color?: string;
}

const num = (value: number) => (value || 0).toLocaleString();
const pct = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : 0);
const formatDate = (value: string) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(`${value}T12:00:00Z`)
  );

const ProgressRow: React.FC<ProgressRowProps> = ({
  label,
  count,
  total,
  icon: Icon,
  color = 'bg-indigo-500',
}) => {
  const percentage = pct(count, total);

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
        <span className="flex min-w-0 items-center gap-2 text-gray-700">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" />}
          <span className="truncate">{label}</span>
        </span>
        <span className="shrink-0 font-medium text-gray-900">
          {num(count)} <span className="font-normal text-gray-400">· {percentage}%</span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const NewUsersBreakdownModal: React.FC<Props> = ({
  range,
  data,
  isLoading,
  error,
  onClose,
  onRetry,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const createdAccount = data?.cohort.createdAccount ?? 0;
  const firstTimeUsers = data?.cohort.firstTimeUsers ?? 0;
  const statusRows = data
    ? [
        {
          label: 'Continued as guest',
          value: data.cohort.continuedAsGuest,
          percentage: pct(data.cohort.continuedAsGuest, firstTimeUsers),
          icon: Users,
          color: 'bg-gray-400',
        },
        {
          label: 'Created an account',
          value: createdAccount,
          percentage: pct(createdAccount, firstTimeUsers),
          icon: UserPlus,
          color: 'bg-indigo-500',
        },
        {
          label: 'Existing account sign-in',
          value: data.cohort.existingAccount,
          percentage: pct(data.cohort.existingAccount, firstTimeUsers),
          icon: LogIn,
          color: 'bg-cyan-500',
        },
      ]
    : [];

  const providers = data
    ? [
        { label: 'Email & password', count: data.signupProviders.emailPassword, icon: Mail },
        { label: 'Apple', count: data.signupProviders.apple },
        { label: 'Google', count: data.signupProviders.google },
        ...(data.signupProviders.facebook > 0
          ? [{ label: 'Facebook', count: data.signupProviders.facebook }]
          : []),
        ...(data.signupProviders.other > 0
          ? [{ label: 'Other / unknown', count: data.signupProviders.other }]
          : []),
      ]
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-users-modal-title"
        className="max-h-[calc(100vh-2rem)] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-white px-6 py-5">
          <div>
            <h2 id="new-users-modal-title" className="text-xl font-bold text-gray-900">
              New app users
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              First launched {formatDate(range.startDate)} – {formatDate(range.endDate)} · latest known status
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close new-user breakdown"
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {isLoading && (
            <div className="animate-pulse space-y-5">
              <div className="h-32 rounded-lg bg-gray-100" />
              <div className="grid gap-5 md:grid-cols-2">
                <div className="h-64 rounded-lg bg-gray-100" />
                <div className="h-64 rounded-lg bg-gray-100" />
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
              <p className="text-sm font-medium text-red-700">Could not load the new-user breakdown.</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
              >
                Try again
              </button>
            </div>
          )}

          {data && !isLoading && (
            <>
              <section className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-5">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-indigo-700">First-time users</p>
                    <p className="mt-1 text-4xl font-bold text-gray-900">{num(firstTimeUsers)}</p>
                  </div>
                  <p className="max-w-md text-xs leading-5 text-gray-500">
                    Each device is counted from its first app launch in this period, then classified by its latest known account state.
                  </p>
                </div>

                {firstTimeUsers > 0 && (
                  <div className="mt-5 flex h-2 overflow-hidden rounded-full bg-gray-100">
                    {statusRows.map((row) => (
                      <div
                        key={row.label}
                        className={row.color}
                        style={{ width: `${row.percentage}%` }}
                        title={`${row.label}: ${row.value}`}
                      />
                    ))}
                  </div>
                )}

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {statusRows.map((row) => {
                    const Icon = row.icon;
                    return (
                      <div key={row.label} className="rounded-lg border border-white bg-white/80 p-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Icon className="h-4 w-4" />
                          {row.label}
                        </div>
                        <p className="mt-2 text-xl font-semibold text-gray-900">
                          {num(row.value)}
                          <span className="ml-1 text-sm font-normal text-gray-400">· {row.percentage}%</span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="grid gap-5 md:grid-cols-2">
                <section className="rounded-lg border p-5">
                  <div className="mb-5">
                    <h3 className="font-semibold text-gray-900">How they signed up</h3>
                    <p className="mt-1 text-xs text-gray-500">Of {num(createdAccount)} accounts created</p>
                  </div>
                  {createdAccount > 0 ? (
                    <div className="space-y-4">
                      {providers.map((provider) => (
                        <ProgressRow
                          key={provider.label}
                          label={provider.label}
                          count={provider.count}
                          total={createdAccount}
                          icon={provider.icon}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No accounts were created by this cohort yet.</p>
                  )}
                </section>

                <section className="rounded-lg border p-5">
                  <div className="mb-5">
                    <h3 className="font-semibold text-gray-900">Setup signals</h3>
                    <p className="mt-1 text-xs text-gray-500">Current state of {num(createdAccount)} accounts created</p>
                  </div>
                  {createdAccount > 0 ? (
                    <div className="space-y-4">
                      <ProgressRow
                        label="Onboarding complete"
                        count={data.setup.onboardingCompleted}
                        total={createdAccount}
                        icon={CheckCircle2}
                        color="bg-emerald-500"
                      />
                      <ProgressRow
                        label="License plate added"
                        count={data.setup.licensePlateAdded}
                        total={createdAccount}
                        icon={Car}
                        color="bg-cyan-500"
                      />
                      <ProgressRow
                        label="Payment method added"
                        count={data.setup.paymentMethodAdded}
                        total={createdAccount}
                        icon={CreditCard}
                        color="bg-violet-500"
                      />
                      <div className="border-t pt-4">
                        <ProgressRow
                          label="Marketing opt-in"
                          count={data.setup.marketingOptIn}
                          total={createdAccount}
                          icon={Megaphone}
                          color="bg-gray-400"
                        />
                        <p className="mt-2 text-xs text-gray-400">Optional consent, not an onboarding requirement.</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Setup signals will appear after an account is created.</p>
                  )}
                </section>
              </div>

              <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-4 py-3 text-xs leading-5 text-gray-500">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                Setup signals overlap and do not add up to 100%. Users who sign into an account created before their first launch on this device appear under existing account sign-in.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewUsersBreakdownModal;
